import { Modal, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { get } from 'lodash-es';

import StatusSwitch from 'components/StatusSwitch';
import { updateOrgConfig } from 'services/api/admin';
import { queries } from 'services/queries';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

/** AI 即时精准回复开关（写入 orgConfig.enableAI，关闭需二次确认） */
export const AIEnableStatusSwitch = () => {
  const { t } = useTranslate();
  const { data } = useQuery(queries.accounts.orgConfig);
  const queryClient = useQueryClient();

  const update = (enableAI: boolean) =>
    updateOrgConfig({ enableAI }).then(() => {
      queryClient.invalidateQueries(queries.accounts.orgConfig.queryKey);
    });

  return (
    <StatusSwitch
      checked={get(data, 'enableAI')}
      onChange={(enable) => {
        if (enable) {
          return update(enable);
        }
        return new Promise((resolve, reject) => {
          Modal.confirm({
            title: t('确认要关闭吗？'),
            content: t('关闭后，将停止AI精准回复'),
            onOk: () =>
              update(enable)
                .then(() => resolve(null))
                .catch(reject),
            onCancel: () => resolve(null),
          });
        });
      }}
    />
  );
};

/**
 * 订购页右上角（试用/无套餐客户）的 AI 即时精准回复开关入口：
 * 有套餐客户的开关在「服务概况」页，订购页已移除对应模块，这里兜底提供开启入口
 */
export const AITrialSwitch = () => {
  const { t } = useTranslate();
  const { qaEnable, whiteListConfig: { LLM } = {} } = useOrgConfigStateValue();

  // 与「服务概况」页开关同源：未开通 LLM 白名单或已启用 QA 时不展示
  if (!LLM || qaEnable) return null;

  return (
    <div className="flex items-center gap-[8px]">
      <span className="flex items-center gap-[4px]">
        {t('AI精准回复能力护航的订单数')}
        {/* 以问号图标为触发区，浮层右边缘与图标对齐、向下展开；
            antd v4 气泡根节点默认 max-width 250px，宽度须用 overlayStyle 压过它 */}
        <Tooltip
          placement="bottomRight"
          overlayStyle={{ width: 540, maxWidth: 540 }}
          title={
            <>
              <div className="font-medium">{t('什么是 AI 精准回复能力护航的订单数')}</div>
              <div>{t('AI精准回复说明')}</div>
            </>
          }
        >
          <QuestionCircleOutlined className="cursor-pointer text-[var(--fett-text-secondary)]" />
        </Tooltip>
      </span>
      <AIEnableStatusSwitch />
    </div>
  );
};
